// @flow
import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import {
  DateTime,
} from 'luxon';

import type {
  Id,
} from 'chronos-types';

import * as actions from 'chronos-actions';
import * as selectors from 'chronos-selectors';
import {
  jiraApi,
} from 'chronos-api';
import {
  jts,
} from 'chronos-utils';

import {
  notify,
} from './ui';
import {
  throwError,
} from './helpers';


export function* getAdditionalWorklogsForIssues(
  incompleteIssues: Array<any>,
): Generator<*, *, *> {
  try {
    const worklogsArr = yield eff.all(
      incompleteIssues.map(
        i => (
          eff.call(
            jiraApi.getIssueWorklogs,
            {
              params: {
                issueIdOrKey: i.id,
              },
            },
          )
        ),
      ),
    );
    const worklogs = (
      worklogsArr.reduce(
        (acc, w) => ([
          ...acc,
          ...w.worklogs,
        ]),
        [],
      )
    );
    const issues = incompleteIssues.map((issue) => {
      const additionalWorklogs = worklogs.filter(w => w.issueId === issue.id);
      if (additionalWorklogs.length) {
        return {
          ...issue,
          fields: {
            ...issue.fields,
            worklog: {
              total: additionalWorklogs.length,
              worklogs: additionalWorklogs,
            },
          },
        };
      }
      return issue;
    });
    return issues;
  } catch (err) {
    throwError(err);
    return incompleteIssues;
  }
}

export function* saveWorklog({
  issueId,
  worklogId,
  comment,
  startTime,
  adjustEstimate,
  newEstimate,
  reduceBy,
  timeSpent,
  timeSpentInSeconds,
}): Generator<*, *, *> {
  const worklogsActions = createActionCreators(
    worklogId ? 'update' : 'create',
    {
      resourceType: 'worklogs',
      request: 'saveWorklog',
    },
  );
  const issuesActions = createActionCreators(
    'update',
    {
      resourceType: 'issues',
      request: 'updateIssue',
      list: 'issues',
    },
  );
  const jiraType = yield eff.select(selectors.getUiState('jiraType'));
  try {
    yield eff.put(worklogsActions.pending());
    if (!worklogId) {
      yield eff.put(issuesActions.pending());
    }
    yield eff.put(actions.setUiState({
      worklogFormModalIsOpen: false,
    }));
    yield eff.fork(notify, {
      resourceType: 'worklogs',
      request: 'saveWorklog',
      spinnerTitle: worklogId ? 'Edit worklog' : 'Add worklog',
      title: worklogId ? 'Successfully edited worklog' : 'Successfully added worklog',
    });
    const timeSpentSeconds = timeSpentInSeconds || jts(timeSpent);
    if (timeSpentSeconds < 60) {
      yield eff.cancel();
    }

    const params = {
      worklogId,
      issueIdOrKey: issueId,
      adjustEstimate,
    };

    const body = {
      started: (
        DateTime.fromISO(startTime).toUTC().toISO().replace('Z', '+0000')
      ),
      timeSpentSeconds,
      ...(
        comment.length
          ? ({
            comment: (
              jiraType === 'server'
                ? comment
                : ({
                  type: 'doc',
                  version: 1,
                  content: [
                    {
                      type: 'paragraph',
                      content: [
                        {
                          type: 'text',
                          text: comment,
                        },
                      ],
                    },
                  ],
                })),
          }) : {}
      ),
    };
    if (adjustEstimate === 'new') {
      params.newEstimate = newEstimate;
    }
    if (adjustEstimate === 'manual') {
      params.reduceBy = reduceBy;
    }
    const worklog = yield eff.call(
      worklogId
        ? (
          jiraApi.updateIssueWorklog
        ) : (
          jiraApi.addIssueWorklog
        ),
      {
        params,
        body,
      },
    );
    yield eff.put(worklogsActions.succeeded({
      resources: [worklog],
    }));

    const issuesMap = yield eff.select(selectors.getResourceMap('issues'));
    const updatedIssue = yield eff.call(
      jiraApi.getIssueByIdOrKey,
      {
        params: {
          issueIdOrKey: issueId,
        },
      },
    );
    const issue = issuesMap[issueId];
    yield eff.put(issuesActions.succeeded({
      resources: [{
        ...updatedIssue,
        fields: {
          ...updatedIssue.fields,
          worklogs: [
            ...new Set([
              worklog.id,
              ...(
                issue?.fields?.worklogs || []
              ),
            ]),
          ],
        },
      }],
    }));
  } catch (err) {
    throwError(err);
  }
}

export function* deleteWorklog({ worklogId }: {
  worklogId: Id,
}): Generator<*, void, *> {
  const worklogsA = createActionCreators('delete', {
    resourceType: 'worklogs',
    request: 'deleteWorklog',
  });
  const issuesA = createActionCreators('update', {
    resourceType: 'issues',
    request: 'deleteIssue',
  });
  try {
    yield eff.put(worklogsA.pending());
    yield eff.put(issuesA.pending());
    yield eff.fork(notify, {
      resourceType: 'worklogs',
      request: 'deleteWorklog',
      spinnerTitle: 'Delete worklog',
      title: 'Successfully deleted worklog',
    });

    const worklogsMap = yield eff.select(selectors.getResourceMap('worklogs'));
    const issuesMap = yield eff.select(selectors.getResourceMap('issues'));
    const worklog = worklogsMap[worklogId];
    const issue = issuesMap[worklog.issueId];

    const params = {
      issueIdOrKey: worklog.issueId,
      worklogId,
      adjustEstimate: 'auto',
    };
    yield eff.call(
      jiraApi.deleteIssueWorklog,
      {
        params,
      },
    );
    yield eff.put(worklogsA.succeeded({
      resources: [worklog.id],
    }));
    yield eff.put(issuesA.succeeded({
      resources: [{
        ...issue,
        fields: {
          ...issue.fields,
          worklogs: issue.fields.worklogs.filter(wid => wid !== worklogId),
        },
      }],
    }));
  } catch (err) {
    yield eff.fork(notify, {
      title: 'Failed to delete worklog',
    });
    throwError(err);
  }
}

export function* takeDeleteWorklogRequest(): Generator<*, void, *> {
  yield eff.takeEvery(
    actions.actionTypes.DELETE_WORKLOG_REQUEST,
    deleteWorklog,
  );
}

export function* takeSaveWorklogRequest(): Generator<*, void, *> {
  yield eff.takeEvery(
    actions.actionTypes.SAVE_WORKLOG_REQUEST,
    saveWorklog,
  );
}
